import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import { Close } from "@mui/icons-material";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import ViewLayout from "./ViewLayout";
import UpdateLayout from "./UpdateLayout";
import AddItemModal from "../Items/AddItemModal";
import {
  useDeleteItemMutation,
  useGetAllItemsQuery,
  useGetPartnerZonesQuery,
  usePartnersQuery,
} from "../../Slices/roomNateApis";
import { baseUrl } from "../../utils/baseUrl";
import { useGlobalAuthContext } from "../../context/AuthContext";
import GroupedGrid from "../GlobalStyles/GroupedGrid";
import { useGlobalContext } from "../../context/GlobalContext";
import { ReactComponent as LogoIcon } from "../../Assets/mf-logo.svg";

// ── Sort options ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "newest", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
  { value: "name-asc", label: "Name: A → Z" },
  { value: "name-desc", label: "Name: Z → A" },
  { value: "size-desc", label: "Largest (L × W)" },
  { value: "size-asc", label: "Smallest (L × W)" },
];

function getMaxArea(row) {
  const variants = row?.variants ?? [];
  let max = 0;
  variants.forEach((v) => {
    if (v.width && v.length) max = Math.max(max, v.width * v.length);
  });
  return max;
}

function sortLayouts(items, sortBy) {
  if (!items) return [];
  const arr = [...items];
  switch (sortBy) {
    case "name-asc":
      return arr.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    case "name-desc":
      return arr.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? ""));
    case "oldest":
      return arr.sort((a, b) => (a._id ?? "").localeCompare(b._id ?? ""));
    case "size-desc":
      return arr.sort((a, b) => getMaxArea(b) - getMaxArea(a));
    case "size-asc":
      return arr.sort((a, b) => getMaxArea(a) - getMaxArea(b));
    case "newest":
    default:
      return arr.sort((a, b) => (b._id ?? "").localeCompare(a._id ?? ""));
  }
}

// ── Wall attachment helpers ───────────────────────────────────────────────────

// Cardinal + intercardinal directions shown on cards (in compass order)
const WALL_DIRS = [
  { key: "north", abbr: "N" },
  { key: "northeast", abbr: "NE" },
  { key: "east", abbr: "E" },
  { key: "southeast", abbr: "SE" },
  { key: "south", abbr: "S" },
  { key: "southwest", abbr: "SW" },
  { key: "west", abbr: "W" },
  { key: "northwest", abbr: "NW" },
];

function getWallChips(wallAttachments, connectionPointsOptionsData) {
  if (!wallAttachments || !connectionPointsOptionsData) return [];
  return WALL_DIRS.map(({ key, abbr }) => {
    const val = wallAttachments[key];
    if (val == null) return null;
    const label =
      connectionPointsOptionsData.find((o) => o.value === val)?.label ?? "";
    const lower = label.toLowerCase();
    if (!lower.includes("must")) return null; // only show meaningful constraints
    const isMustNot = lower.includes("must not");
    return { abbr, isMustNot, label };
  }).filter(Boolean);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRange(min, max, prefix) {
  if (!min && !max) return null;
  return min === max ? `${prefix}: ${min}` : `${prefix}: ${min}–${max}`;
}

function ImagePlaceholder() {
  return (
    <Box
      sx={{
        width: "100%",
        height: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.04)",
        color: "rgba(0,0,0,0.18)",
      }}
    >
      <BrokenImageOutlinedIcon />
    </Box>
  );
}

// ── Layout card ───────────────────────────────────────────────────────────────

function LayoutCard({
  row,
  zonesData,
  open,
  selectedId,
  handleClickOpen,
  handleClose,
  handleDelete,
  openEditModal,
  selectedRow,
  handleOpenEditModal,
  handleCloseEditModal,
  connectionPointsOptionsData,
  connectionPointsData,
  shapesData,
  allItems,
  partnersData,
}) {
  const variants = row?.variants ?? [];
  const widths = variants.map((v) => v.width).filter(Boolean);
  const heights = variants.map((v) => v.length).filter(Boolean);
  const minW = widths.length ? Math.min(...widths) : null;
  const maxW = widths.length ? Math.max(...widths) : null;
  const minH = heights.length ? Math.min(...heights) : null;
  const maxH = heights.length ? Math.max(...heights) : null;

  const widthLabel = formatRange(minW, maxW, "X");
  const heightLabel = formatRange(minH, maxH, "Y");

  const wallChips = getWallChips(row.wallAttachments, connectionPointsOptionsData);

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Images ── */}
      <Box sx={{ display: "flex", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        {/* Front view */}
        <Box sx={{ flex: 1, borderRight: "1px solid rgba(0,0,0,0.06)" }}>
          {row?.frontViewImage ? (
            <img
              src={`${baseUrl}/g${row.frontViewImage}`}
              alt="front"
              style={{
                width: "100%",
                height: 140,
                objectFit: "contain",
                display: "block",
                backgroundColor: "#f7f7f7",
              }}
            />
          ) : (
            <ImagePlaceholder />
          )}
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.65rem",
              color: "text.disabled",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              py: 0.4,
              bgcolor: "#fafafa",
            }}
          >
            Front
          </Typography>
        </Box>

        {/* Top view */}
        <Box sx={{ flex: 1 }}>
          {row?.topViewImage ? (
            <img
              src={`${baseUrl}/g${row.topViewImage}`}
              alt="top"
              style={{
                width: "100%",
                height: 140,
                objectFit: "contain",
                display: "block",
                backgroundColor: "#f7f7f7",
              }}
            />
          ) : (
            <ImagePlaceholder />
          )}
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.65rem",
              color: "text.disabled",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              py: 0.4,
              bgcolor: "#fafafa",
            }}
          >
            Top
          </Typography>
        </Box>
      </Box>

      {/* ── Metadata chips ── */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flexGrow: 1 }}>
        {/* Zone chips */}
        {row.zones?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {row.zones.map((zone, i) => {
              const label =
                row.partnerZonesDetails?.find((z) => z?.value === zone)?.label ??
                zonesData?.find((z) => z?.value === zone)?.label ??
                zone;
              return (
                <Chip
                  key={i}
                  label={label}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    bgcolor: "rgba(0,0,0,0.06)",
                    border: "none",
                    fontWeight: 500,
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Partner chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {row.partners?.length > 0 ? (
            row.partners.map((pid, i) => {
              const title = partnersData?.find((p) => p._id === pid)?.title ?? pid;
              return (
                <Chip
                  key={i}
                  label={title.toUpperCase()}
                  size="small"
                  sx={{
                    fontSize: "0.68rem",
                    height: 22,
                    fontWeight: 700,
                    bgcolor: "rgba(0,0,0,0.06)",
                    border: "none",
                    letterSpacing: "0.04em",
                  }}
                />
              );
            })
          ) : (
            <Chip
              icon={
                <LogoIcon
                  width={12}
                  height={12}
                  style={{ marginLeft: 6, flexShrink: 0 }}
                />
              }
              label="MF"
              size="small"
              sx={{
                fontSize: "0.68rem",
                height: 22,
                fontWeight: 700,
                bgcolor: "rgba(0,0,0,0.06)",
                border: "none",
                letterSpacing: "0.04em",
              }}
            />
          )}
        </Box>

        {/* Stat chips: Likelihood · Y · X */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: wallChips.length > 0 ? 1 : 0 }}>
          {row.likelihood != null && (
            <Chip
              label={`~ ${row.likelihood}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          )}
          {heightLabel && (
            <Chip
              label={heightLabel}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          )}
          {widthLabel && (
            <Chip
              label={widthLabel}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          )}
        </Box>

        {/* Wall attachment chips — only directions with a must/must-not rule */}
        {wallChips.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {wallChips.map(({ abbr, isMustNot, label }) => (
              <Tooltip key={abbr} title={`${abbr}: ${label}`} placement="top">
                <Chip
                  label={`${abbr}: ${isMustNot ? "Must Not" : "Must"}`}
                  size="small"
                  sx={{
                    fontSize: "0.68rem",
                    height: 22,
                    fontWeight: 500,
                    bgcolor: isMustNot
                      ? "rgba(211,47,47,0.08)"
                      : "rgba(46,125,50,0.08)",
                    color: isMustNot ? "#c62828" : "#2e7d32",
                    border: "none",
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Layout items */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {row.layoutItems?.length > 0 ? (
            row.layoutItems.map((li, i) => {
              const item = allItems?.find((it) => it._id === li._id);
              const name = item?.name ?? "Item";
              return (
                <Chip
                  key={i}
                  label={`${name} ×${li.capacity ?? 1}`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    bgcolor: "rgba(0,0,0,0.06)",
                    border: "none",
                    fontWeight: 500,
                  }}
                />
              );
            })
          ) : (
            <Chip
              label="No Items"
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                fontWeight: 600,
                bgcolor: "rgba(255,171,0,0.12)",
                color: "#b76e00",
                border: "none",
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Actions: Preview → Edit → Delete ── */}
      <Divider />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          px: 1,
          py: 0.5,
          gap: 0.25,
        }}
      >
        {/* Preview */}
        <ViewLayout
          items={row}
          connectionPointsOptionsData={connectionPointsOptionsData}
          zonesData={zonesData}
        />

        {/* Edit */}
        <Tooltip title="Edit">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenEditModal(row._id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Delete */}
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleClickOpen(row._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Delete confirmation */}
        {open && selectedId === row._id && (
          <Dialog
            maxWidth="sm"
            fullWidth
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirm the Action?"}
            </DialogTitle>
            <Box position="absolute" top={10} right={0}>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Box>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Do you really want to delete the layout?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button onClick={() => handleDelete(row._id)} variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Edit modal */}
        {openEditModal && selectedRow === row._id && (
          <UpdateLayout
            items={row}
            handleClose={handleCloseEditModal}
            open={openEditModal}
            zonesData={zonesData}
            shapesData={shapesData}
            connectionPointsData={connectionPointsData}
            connectionPointsOptionsData={connectionPointsOptionsData}
          />
        )}
      </Box>
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function LayoutTable({
  connectionPointsOptionsData,
  shapesData,
  zonesData,
  connectionPointsData,
  gridView,
}) {
  const { activePartner } = useGlobalContext();
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const [selectedId, setSelectedId] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("newest");

  const {
    data: FetchItems,
    refetch,
    isFetching,
  } = useGetAllItemsQuery({ itemType: 2, activePartner });

  // Fetch all individual items (type 1) to resolve layoutItems IDs → names
  const { data: allItemsData } = useGetAllItemsQuery({ itemType: 1 });
  // Fetch all partners to resolve partner IDs → titles
  const { data: partnersData } = usePartnersQuery();

  // When a partner is active, use their zone definitions for the filter
  // dropdown so the zone values match what's stored on their layouts.
  const { data: partnerZones, error: partnerZonesError } = useGetPartnerZonesQuery(
    activePartner,
    { skip: !activePartner }
  );
  const effectiveZonesData =
    activePartner && !partnerZonesError && partnerZones?.length > 0
      ? partnerZones.filter((z) => z?.value !== "custom")
      : zonesData;

  const [selectedZone, setSelectedZone] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);

  const [deleteItem, resultStats] = useDeleteItemMutation();
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [openEditModal, setOpenEditModal] = React.useState(false);

  const handleClickOpen = (id) => {
    setOpen(true);
    setSelectedId(id);
  };

  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
    }
    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
  }, [resultStats]);

  const handleDelete = () => {
    handleClose();
    deleteItem(selectedId);
    setOpen(false);
  };

  const filteredData = React.useMemo(() => {
    const filtered = FetchItems?.items?.filter((item) => {
      if (!selectedZone) return true;
      return item.zones.map((z) => z.toLowerCase()).includes(selectedZone.toLowerCase());
    });
    return sortLayouts(filtered, sortBy);
  }, [FetchItems?.items, selectedZone, sortBy]);

  const groupedData = React.useMemo(
    () =>
      filteredData?.reduce((acc, item) => {
        item.zones.forEach((zone) => {
          if (!acc[zone]) acc[zone] = [];
          acc[zone].push(item);
        });
        return acc;
      }, {}),
    [filteredData]
  );

  React.useEffect(() => {
    refetch();
  }, [currentUserSessionStorage, refetch]);

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedRow(null);
  };

  const handleOpenEditModal = (id) => {
    setOpenEditModal(true);
    setSelectedRow(id);
  };

  const cardProps = {
    open,
    selectedId,
    handleClickOpen,
    handleClose,
    handleDelete,
    openEditModal,
    selectedRow,
    handleOpenEditModal,
    handleCloseEditModal,
    connectionPointsOptionsData,
    connectionPointsData,
    shapesData,
    zonesData,
    allItems: allItemsData?.items,
    partnersData,
  };

  return (
    <>
      {successAlert && <SuccessAlert title="Layout has been deleted successfully!" />}
      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {gridView ? (
        <Box>
          {isFetching ? (
            <Typography>Loading...</Typography>
          ) : (
            <GroupedGrid groupedData={groupedData} />
          )}
        </Box>
      ) : (
        <Box>
          {/* Filters row */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              {effectiveZonesData && (
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    options={effectiveZonesData}
                    fullWidth
                    onChange={(_, value) =>
                      setSelectedZone(value?.value ?? null)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Filter by Zone" size="small" />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="layout-sort-label">Sort by</InputLabel>
                  <Select
                    labelId="layout-sort-label"
                    value={sortBy}
                    label="Sort by"
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Count + Add button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8125rem" }}
            >
              {isFetching ? "Loading…" : `${filteredData?.length ?? 0} layouts`}
            </Typography>

            <AddItemModal
              defaultType={2}
              zonesData={zonesData}
              connectionPointsData={connectionPointsData}
              connectionPointsOptionsData={connectionPointsOptionsData}
            />
          </Box>

          {/* Card grid */}
          {!isFetching && filteredData?.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No layouts found.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredData?.map((row, index) => (
                <Grid item xs={12} sm={6} lg={4} key={row._id ?? index}>
                  <LayoutCard row={row} {...cardProps} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </>
  );
}
